import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, LockKey } from "@phosphor-icons/react";
import { useAuth, formatApiErrorDetail } from "../lib/AuthContext";
import SEO from "../components/SEO";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_brand-builders-43/artifacts/6hieg182_Artboard%2010.svg";

export default function AdminLogin() {
    const { user, loading, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-scd text-cream">
                <span className="label-eyebrow text-cream/60">Chargement…</span>
            </div>
        );
    }
    if (user) return <Navigate to="/admin/briefs" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await login(email, password);
            navigate("/admin/briefs");
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main
            data-testid="admin-login-page"
            className="min-h-screen bg-scd text-cream flex items-center justify-center px-6 py-12 grain"
        >
            <SEO title="Connexion administration" path="/admin" noindex={true} />
            <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="w-full max-w-md"
            >
                <div className="text-center mb-10">
                    <img
                        src={LOGO_URL}
                        alt="Studio Cent Douze"
                        className="h-10 w-auto mx-auto"
                    />
                    <span className="label-eyebrow text-yellow mt-6 block">
                        — Espace administration
                    </span>
                    <h1 className="font-display text-4xl mt-4">Connexion</h1>
                    <p className="text-cream/65 text-sm mt-3">
                        Accès réservé à l'équipe Studio Cent Douze.
                    </p>
                </div>

                <form
                    onSubmit={onSubmit}
                    className="border border-scd bg-elev rounded-sm p-7 space-y-5"
                    data-testid="admin-login-form"
                >
                    <label className="block">
                        <span className="label-eyebrow text-cream/70 mb-2 block">
                            Courriel
                        </span>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="admin-login-email"
                            required
                            autoFocus
                            className="scd-input"
                            placeholder="admin@studiocentdouze.com"
                        />
                    </label>
                    <label className="block">
                        <span className="label-eyebrow text-cream/70 mb-2 block">
                            Mot de passe
                        </span>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="admin-login-password"
                            required
                            className="scd-input"
                            placeholder="••••••••"
                        />
                    </label>

                    {error && (
                        <div
                            data-testid="admin-login-error"
                            className="text-sm text-[#ff6b6b] border border-[#ff6b6b]/30 bg-[#ff6b6b]/10 rounded-sm px-3 py-2"
                        >
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="admin-login-submit"
                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-yellow text-[#272727] font-semibold hover:bg-green hover:text-[#faf7f1] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        <LockKey size={18} weight="bold" />
                        {submitting ? "Connexion…" : "Se connecter"}
                        <ArrowUpRight size={16} weight="bold" />
                    </button>
                </form>

                <div className="mt-6 text-center">
                    <a
                        href="/"
                        className="text-cream/60 hover:text-yellow text-sm hover-underline"
                        data-testid="admin-back-home"
                    >
                        ← Retour au site
                    </a>
                </div>
            </motion.div>

            <style>{`
                .scd-input {
                    width: 100%;
                    background: #faf7f1;
                    color: #272727;
                    border: 1px solid rgba(39,39,39,0.18);
                    padding: 0.85rem 1rem;
                    border-radius: 4px;
                    font-family: "Manrope", sans-serif;
                    font-size: 0.95rem;
                    transition: border-color 0.25s ease, box-shadow 0.25s ease;
                }
                .scd-input:focus {
                    outline: none;
                    border-color: #3b5838;
                    box-shadow: 0 0 0 3px rgba(59,88,56,0.18);
                }
                .scd-input::placeholder { color: rgba(39,39,39,0.4); }
            `}</style>
        </main>
    );
}
